import { Icon, Typography, TypographyPropsVariantOverrides } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import { Box, SxProps, Theme } from '@mui/system';
import { OverridableStringUnion } from '@mui/types';
import {
  getRouteName,
  OperationCompanyOrderTypeOption,
  RootState,
  selectAllCustomerOrderTypeOptions,
  selectCustomerOrderTypeOptionsById,
  updateOrderEntryHeaderOperationCompanyOrderType,
  useAppDispatch,
  useAppSelector,
} from 'common';
import { OrderEntryType } from 'common/src/models/order.enums';
import { FC, useState } from 'react';
import { CfContainedButton } from '../../../../../cf-ui/buttons/CfContainedButton';
import TypographySkeleton from '../../../../_shared/components/TypographySkeleton';
import { StyledLabelTypography } from '../OrderSummary.styles';
import { OrderTypeSelect } from './OrderTypeSelect';

export type EditablePurchaseOrderNumberProps = {
  variant: OverridableStringUnion<Variant, TypographyPropsVariantOverrides>;
  labelVariant?: OverridableStringUnion<Variant, TypographyPropsVariantOverrides>;
  buttonContainerSx?: SxProps<Theme>;
  testId?: string;
  iconSize?: 'small' | 'medium';
  iconSx?: SxProps<Theme>;
};

export const EditableOrderType: FC<EditablePurchaseOrderNumberProps> = (
  props: EditablePurchaseOrderNumberProps
): JSX.Element => {
  const dispatch = useAppDispatch();

  // selectAllCustomerOrderTypeOptions
  const isLoading = useAppSelector((x: RootState) => !x.orders.order);
  const isEditOrder = useAppSelector((x: RootState) => x.orders.order?.OrderType === OrderEntryType.Edit);

  const operationCompanyOrderTypeId =
    useAppSelector((x: RootState) => x.orders.order?.OperationCompanyOrderTypeId) || '';

  const optionCount = useAppSelector(
    (x: RootState) => selectAllCustomerOrderTypeOptions(x.customer.operationCompanyOrderTypes).length
  );
  const selectedOption = useAppSelector((x: RootState) =>
    selectCustomerOrderTypeOptionsById(x.customer.operationCompanyOrderTypes, operationCompanyOrderTypeId)
  );

  // Customer lost access to order type
  const hasInvalidOrderType = selectedOption.OperationCompanyOrderTypeId !== operationCompanyOrderTypeId;

  const [editMode, setEditMode] = useState<boolean>(false);
  const [data, setData] = useState<OperationCompanyOrderTypeOption | undefined>(undefined);

  const handleEdit = () => {
    setEditMode(true);
    setData(selectedOption);
  };
  const handleCancel = () => {
    setEditMode(false);
    setData(undefined);
  };

  const handleSubmit = () => {
    if (data !== undefined && data.OperationCompanyOrderTypeId !== operationCompanyOrderTypeId) {
      dispatch(
        updateOrderEntryHeaderOperationCompanyOrderType(
          data.OperationCompanyOrderTypeId,
          data.OrderTypeName,
          getRouteName()
        )
      );
    }
    setEditMode(false);
    setData(undefined);
  };

  if (optionCount <= 1 && !hasInvalidOrderType) return <></>;
  return (
    <Box sx={{}}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <StyledLabelTypography variant='responsiveParagraph'>Order type:</StyledLabelTypography>
        {!isLoading ? (
          <>
            <Typography
              variant={!hasInvalidOrderType ? 'responsiveParagraph' : 'responsiveParagraphBold'}
              sx={{ color: hasInvalidOrderType ? (theme) => theme.palette.error.main : undefined }}
            >
              {!editMode && (hasInvalidOrderType ? 'NA' : selectedOption?.OrderTypeName)}
            </Typography>
            {!isEditOrder && !editMode && (
              <Icon
                className='fa-pencil-alt'
                fontSize={props.iconSize ?? 'small'}
                sx={{
                  lineHeight: (theme) => `${theme.typography[props.variant].lineHeight} !important`,
                  cursor: 'pointer',
                  color: (theme) => theme.palette.common.black,
                  ml: 1,
                  ...props.iconSx,
                }}
                tabIndex={0}
                onClick={handleEdit}
                onKeyPress={(e: React.KeyboardEvent<HTMLSpanElement>) => e.key === 'Enter' && (() => setEditMode(true))}
              />
            )}
          </>
        ) : (
          <TypographySkeleton charCount={10} />
        )}
      </Box>
      {editMode && (
        <Box sx={{ gap: 1, display: 'flex', flexDirection: { xs: 'column', lg: 'row', xl: 'column' } }}>
          <OrderTypeSelect
            operationCompanyOrderTypeId={operationCompanyOrderTypeId}
            onChange={(o) => setData(o)}
            sx={{ width: { xs: '100%', lg: 332, xl: '100%' } }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
            }}
          >
            <CfContainedButton color='secondary' onClick={handleCancel} sx={{ mr: 1 }}>
              Cancel
            </CfContainedButton>
            <CfContainedButton color='primary' onClick={handleSubmit}>
              Update
            </CfContainedButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};
